import type {
  Block, CardItem, CardsBlock, Cell, ChipCell, Column, NoteBlock, SubCell, TableBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinor as sharedExactMinor, isCurrencyCode } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Ledger & Reconciliation › Chart of accounts (`lg-coa`), read from
 * `GET /v1/admin/ledger/accounts` (go-wallet#386, handler
 * `internal/server/admin_ledger.go`).
 *
 * # Four absences on this screen, and none of them mean the same thing
 *
 * The backend shapes this payload so that four different kinds of "nothing"
 * stay apart on the wire, and the whole value of the screen is that they stay
 * apart on the glass too. Collapsing any pair of them produces a sentence about
 * the money that is false:
 *
 *   - **An empty `scope`.** A singleton account — `fees_revenue`, `pending_out`
 *     — has no discriminator within its class, and the schema's default is `''`
 *     rather than NULL. That is a read fact. A `scope` field this console could
 *     not read at all is not, and rendering both as a blank cell would hide a
 *     malformed row inside the ordinary case.
 *   - **An absent `customer_id`.** `ledger_accounts_customer_iff_wallet` is a
 *     CHECK constraint: a non-wallet class *cannot* carry a customer, so absent
 *     means "not a customer account" and never "unknown". A `0` here would name
 *     customer zero, and a dash would suggest we failed to look.
 *   - **An absent `closed_at`.** The account is open. The field is a pointer in
 *     Go precisely so a zero time never reaches the wire — and a console that
 *     read the zero anyway would date every live account to the year 1.
 *   - **An absent balance.** No `ledger_balances` row means nothing has ever
 *     been posted to the account. The backend says so in a reason and sends no
 *     value; a `0` in that cell is a claim about money we never read.
 *
 * # Nothing on this screen is summed
 *
 * The design corpus draws a trial balance under this table. It is not on this
 * wire, and it is not computed here. Two reasons, and the second is the one
 * that matters: the endpoint returns a *capped page*, so any total taken here
 * would be a total of 200 accounts presented as a total of the chart — and a
 * trial balance that is quietly wrong is worse than one that is absent. The
 * first reason is the standing rule that this console re-derives no figure the
 * ledger already holds (invariant 2; go-wallet's `ledgerread` package doc).
 *
 * # `amount_minor` arrives as a number OR a decimal string
 *
 * go-wallet-admin-client#38 widens the reader ahead of go-wallet#411 flipping
 * the encoding, so both forms have to read identically. `money.ts` does not
 * export `exactMinor` yet — #38 has not merged on this branch's base — so the
 * BigInt reader lives here, in `exactMinor` below, and moves to the money
 * module when it lands. It never routes a string through `Number()` first:
 * `Number()` turns `null` into `0`, `"0x10"` into `16` and `"1e3"` into `1000`,
 * and a fabricated zero balance is the exact failure this whole file is about.
 *
 * # Read-only, structurally
 *
 * No block below carries an action. There is no close, no reopen, no balance
 * adjustment and no freeze on this screen — not because they are unbuilt but
 * because the only thing that moves a ledger balance is a posting, and the only
 * thing that writes a posting is a provider event (invariant 2b).
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_ledger.go json tags
// ---------------------------------------------------------------------------

/**
 * Whether a figure has a value at all.
 *
 * Declared here rather than imported from `dashboard.ts` so this adapter owns
 * its own contract with its own endpoint: the two payloads share a dialect
 * today and `amount_minor` already differs between them, which is what a shared
 * type would have hidden.
 */
export type Availability = 'available' | 'not_built' | 'unavailable'

export interface ProvenanceView {
  readonly source: string
  readonly authority: string
  readonly legal: string
  readonly read_at: string
}

/**
 * Minor units and an explicit ISO code, always together (invariants 3 and 4).
 *
 * `amount_minor` is `number | string` for the length of the #38 → go-wallet#411
 * changeover. Both forms must produce the same cell; see `exactMinor`.
 */
export interface MoneyView {
  readonly amount_minor: number | string
  readonly currency: string
}

export interface FigureView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  readonly value?: MoneyView
  readonly provenance?: ProvenanceView
}

/**
 * What the read says about its own completeness.
 *
 * `next_cursor` is present only when `has_more`, because a cursor on a final
 * page is a next-page control that leads nowhere.
 */
export interface PageView {
  readonly limit: number
  readonly returned: number
  readonly has_more: boolean
  readonly next_cursor?: number
}

export interface LedgerAccountView {
  readonly id: number
  readonly class: string
  /** `''` for a singleton account. Never omitted — Go sends it unconditionally. */
  readonly scope: string
  readonly currency: string
  /** Present iff `class` is `customer_wallet`. A schema CHECK, not a convention. */
  readonly customer_id?: number
  readonly created_at: string
  /** Absent while the account is open. */
  readonly closed_at?: string
  readonly balance: FigureView
  /** Absent together with the balance, when no projection row exists. */
  readonly balance_updated_at?: string
  readonly last_posting_id?: number
}

export interface LedgerAccountsResponse {
  readonly generated_at: string
  readonly page: PageView
  readonly accounts: readonly LedgerAccountView[]
}

export const LEDGER_ACCOUNTS_PATH = '/ledger/accounts'

const SOURCE =
  'postgres · ledger_accounts + ledger_balances (GET /v1/admin/ledger/accounts)'

/**
 * The closed set from `internal/ledger`, which is also a Postgres ENUM.
 *
 * Kept only to decide whether a class is one this console has been taught
 * about. A class added to the backend later renders with the value it sent and
 * an attention tone, and is named in the provenance chip — the alternative, a
 * neutral chip, makes an account class nobody has reviewed look like business
 * as usual on the screen that lists every account in the system.
 */
const KNOWN_CLASSES = new Set([
  'customer_wallet',
  'provider_settlement',
  'provider_costs',
  'supplier_costs',
  'gift_card_clearing',
  'fees_revenue',
  'partner_commissions_payable',
  'suspense_discrepancy',
  'pending_out',
])

/** The one class the customer-id CHECK permits a customer on. */
const WALLET_CLASS = 'customer_wallet'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

const AVAILABILITIES = new Set<string>(['available', 'not_built', 'unavailable'])

/**
 * An availability this console does not know becomes `unavailable`.
 *
 * The same posture `availabilityOf` takes in `dashboard.ts`: an unrecognised
 * value resolves to the reading that shows the operator less confidence, never
 * more. A fourth state added to the backend and defaulting to "available" would
 * put an unverified balance on the chart of accounts.
 */
function availabilityOf(raw: string): Availability {
  return AVAILABILITIES.has(raw) ? (raw as Availability) : 'unavailable'
}

/**
 * The backend's reason, verbatim, or the honest stand-in when it sent none.
 *
 * Never paraphrased and never dropped. The difference between "nothing has ever
 * been posted to this account" and "the query failed" decides whether an
 * operator opens a ticket, and only the backend knows which one happened.
 */
function reasonFor(availability: Availability, raw: string, reason: string | undefined): string {
  if (reason !== undefined && reason !== '') return reason
  if (availability === 'available') return ''
  if (!AVAILABILITIES.has(raw)) {
    return `The backend reported availability "${raw}", which this console does not know. Treated as unreadable.`
  }
  return 'Could not be read. This is not a balance of zero.'
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}


/**
 * An integer we can state exactly, from either encoding, or nothing.
 *
 * The string branch NEVER goes through `Number()`. That is not a stylistic
 * preference: `Number(null)` is `0`, `Number("")` is `0`, `Number("0x10")` is
 * `16` and `Number("1e3")` is `1000` — so a coercion-first reader turns four
 * different malformed amounts into confident numbers, one of which is the
 * fabricated zero balance this endpoint is shaped to make impossible. The
 * digits-only regex refuses all four before any arithmetic happens, and BigInt
 * then compares against the safe-integer bound without ever building a double.
 *
 * Returns `number` rather than `bigint` because `AmountCell` is a `number`
 * tuple — which is precisely why the bound has to be checked here. An amount
 * past 2^53 cannot be placed in a cell at all, so it is withheld with a reason
 * instead of printed wrong in its last digits.
 */
/**
 * Delegates to `money.exactMinor`, and does not reimplement it.
 *
 * Four copies of this rule were written for #40, and they had already drifted
 * before the branch was a day old: one accepted a leading `+`, two trimmed
 * whitespace, one returned `bigint`. The rule has a trap in it — validating a
 * string by `Number()`-ing it first accepts `"9007199254740993"`, the one value
 * it exists to refuse — and a rule with a trap is a rule to have exactly once.
 *
 * The shared one is stricter than the copies here were: no surrounding
 * whitespace, no leading `+`. Neither is a shape `strconv.FormatInt` emits, so
 * refusing them narrows the guard rather than breaking a real payload.
 */
function exactMinor(raw: unknown): number | null {
  return sharedExactMinor(raw)
}

/**
 * An integer identifier, in three states.
 *
 * Absent and unusable are different facts about different bugs. An absent
 * `customer_id` is the schema working as designed on a non-wallet account; an
 * id past `Number.MAX_SAFE_INTEGER` has already lost digits inside `JSON.parse`
 * and printing it shows an id that is wrong in its last places and looks
 * entirely plausible — which is how an operator pastes the wrong account into
 * the journal filter.
 */
type IntRead =
  | { readonly kind: 'exact'; readonly value: number }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

function readInt(raw: unknown): IntRead {
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw)) return { kind: 'unreadable' }
  return { kind: 'exact', value: raw }
}

type InstantRead =
  | { readonly kind: 'at'; readonly label: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * An RFC-3339 instant, in the format the design draws timestamps in.
 *
 * The epoch guard is load-bearing on this endpoint rather than defensive.
 * `ComposeAccounts` takes the address of `AccountRow.BalanceUpdatedAt` whenever
 * a projection row exists — including when the column was NULL and the field is
 * still the Go zero — so `balance_updated_at` can reach the wire as
 * `0001-01-01T00:00:00Z`. That parses perfectly well into a date in the year 1,
 * and a "last movement" of the year 1 beside a real balance reads as a
 * timestamp rather than as the absence it is.
 */
function readInstant(raw: unknown): InstantRead {
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'string' || raw === '') return { kind: 'unreadable' }
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return { kind: 'unreadable' }
  return { kind: 'at', label: readAtStamp(at) }
}

/** The screen's own stamp. Unparseable comes back raw rather than invented. */
function stamp(iso: unknown): string {
  if (typeof iso !== 'string' || iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

/** Groups repeated gaps so the provenance chip stays readable at 200 rows. */
function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) =>
    n === 1 ? label : `${label} (${String(n)} accounts)`)
}

// ---------------------------------------------------------------------------
// The balance figure
// ---------------------------------------------------------------------------

/** A balance that can be a cell, or the reason it cannot be one. */
type Placement =
  | { readonly ok: true; readonly minor: number; readonly currency: string }
  | { readonly ok: false; readonly reason: string }

/**
 * Whether one account's balance may be placed in the table, and if not, why.
 *
 * ONE decision, read by the row and by the screen's provenance chip, so the two
 * cannot disagree — the failure `dashboard.ts` shipped with was a figure
 * withheld inside the block while the chip still said `Live`, which claims
 * everything on the screen was read.
 *
 * The last refusal is specific to this endpoint: `ledger_balances.currency` is
 * its own column, so a projection row can in principle be denominated
 * differently from the account it projects. Rendering it would put a JPY amount
 * in a row labelled EUR, which is currency blending inside a single cell
 * (invariant 4). It is a finding, not a display problem, so it is withheld and
 * named rather than coerced.
 */
function placeBalance(f: FigureView, accountCurrency: string | null): Placement {
  const availability = availabilityOf(f.availability)
  if (availability !== 'available') {
    return { ok: false, reason: reasonFor(availability, f.availability, f.reason) }
  }

  // `asRecord`, not `=== undefined`. A `value: null` is typed away by the
  // interface and arrives all the same, and reaching through it for
  // `amount_minor` throws inside a pure composer — which takes the whole screen
  // to the error boundary instead of withholding one cell.
  const value = asRecord(f.value)
  if (value === null) {
    return {
      ok: false,
      reason:
        'The backend reported this balance as available and sent no amount. This is not a balance of zero.',
    }
  }

  // Law 12. There is no component on this console that may render an
  // analytics-sourced balance, and the chart of accounts is the least
  // appropriate place to introduce one: an eventually-consistent mirror figure
  // sitting in a column headed "Ledger balance" is a number an operator will
  // quote.
  if (f.provenance?.authority === 'analytics') {
    return {
      ok: false,
      reason:
        'refused — reported as analytics-sourced, and no balance on this console may come from the analytics mirror (law 12).',
    }
  }

  const minor = exactMinor(value['amount_minor'])
  if (minor === null) {
    return {
      ok: false,
      reason:
        'the amount did not arrive as an integer this console can state exactly, so it is withheld rather than rounded.',
    }
  }

  // The FULL currency predicate, not `currency !== ''`. `formatMinor` throws on
  // anything that is not three letters, and it is called during render — so a
  // `"EU"` off the wire would not withhold a cell, it would take the whole
  // screen to the error boundary.
  const rawCurrency = value['currency']
  if (!isCurrencyCode(rawCurrency)) {
    return {
      ok: false,
      reason:
        rawCurrency === ''
          ? 'the amount arrived with no currency at all, so it cannot be stated (invariant 4).'
          : `the amount arrived with ${JSON.stringify(rawCurrency)}, which is not an ISO-4217 code, so it cannot be stated (invariant 4).`,
    }
  }

  const currency = rawCurrency.trim().toUpperCase()
  if (accountCurrency !== null && currency !== accountCurrency) {
    return {
      ok: false,
      reason:
        `the projection row is denominated in ${currency} and the account in ${accountCurrency}. `
        + 'Two currencies in one position is a finding, not a rounding, so the figure is withheld rather than shown against the wrong code.',
    }
  }

  return { ok: true, minor, currency }
}

// ---------------------------------------------------------------------------
// One account as a row
// ---------------------------------------------------------------------------

/**
 * The columns, drawn from what this endpoint can actually answer.
 *
 * The corpus draws Debit / Credit / Balance and a trial balance beneath. There
 * is no debit or credit on this wire — `ledger_balances` holds one signed
 * projection per account, not a two-sided trial balance — so those columns are
 * gone rather than filled by splitting the balance on its sign, which would
 * invent a bookkeeping presentation the ledger never stated.
 *
 * `Account` is the id and it is mono with a copy affordance because it is the
 * value an operator pastes into the journal's `account_id` filter. Class stays
 * the raw enum value for the same reason: it is what a `class=` filter takes,
 * and a prettified label would not match.
 */
const ACCOUNT_COLS: readonly Column[] = [
  ['Account', 'm', '96px'],
  ['ledger_account_class', 'c', '198px'],
  ['Scope', 's', 'minmax(150px,1fr)'],
  ['Currency', 'x', '84px'],
  ['Customer', 's', '164px'],
  ['Ledger balance', 'a', '152px'],
  ['Last movement', 's', '196px'],
  ['Opened (UTC)', 's', '196px'],
  ['Status', 'c', '96px'],
]

interface RowRead {
  readonly cells: readonly Cell[]
  /** The class as sent, or `null` when the field could not be read at all. */
  readonly cls: string | null
  /** Field-level anomalies, in operator words. */
  readonly gaps: readonly string[]
  /** Balances that could not be placed, each carrying the backend's reason. */
  readonly withheld: readonly string[]
  /** The authority this row's balance claimed, for the screen's chip. */
  readonly authority: string | null
}

/**
 * One account as a row, or `null` when the element is not an object at all.
 *
 * A `null` is counted by the caller and stated on the screen. Dropping it
 * silently would shorten the chart of accounts without saying so, on the one
 * screen whose job is to be the complete list.
 */
function readAccount(raw: unknown, index: number): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null

  const gaps: string[] = []

  const id = readInt(o['id'])
  const ref = id.kind === 'exact' ? `account ${String(id.value)}` : `row ${String(index + 1)}`
  if (id.kind !== 'exact') gaps.push(`${ref}: the account id could not be stated exactly`)

  // Class first: three other cells below can only be judged once we know
  // whether this is a wallet account, and "this console does not know the
  // class" has to stop those judgements rather than let them guess.
  const rawClass = o['class']
  const cls = typeof rawClass === 'string' && rawClass !== '' ? rawClass : null
  const knownClass = cls !== null && KNOWN_CLASSES.has(cls)
  if (cls === null) gaps.push(`${ref}: no account class`)
  else if (!knownClass) gaps.push(`${ref}: class "${cls}", which this console does not know`)
  const classCell: ChipCell = [cls ?? 'unstated', knownClass ? 'n' : 'a']

  // An empty scope is a READ FACT — a singleton account has no discriminator
  // within its class and the column defaults to `''`. A scope field that is not
  // a string at all is not that, and the two must not render as the same blank
  // cell. Go sends `scope` unconditionally, so an absent one is an anomaly.
  const rawScope = o['scope']
  let scopeCell: SubCell
  if (typeof rawScope !== 'string') {
    gaps.push(`${ref}: the scope could not be read, which is not the same as an empty scope`)
    scopeCell = ['—', 'unread — an empty scope would say "singleton"']
  } else if (rawScope === '') {
    scopeCell = ['singleton', 'no scope — one account of this class per currency']
  } else {
    scopeCell = [rawScope]
  }

  const rawCurrency = o['currency']
  const currency = isCurrencyCode(rawCurrency) ? rawCurrency.trim().toUpperCase() : null
  if (currency === null) {
    gaps.push(`${ref}: currency ${JSON.stringify(rawCurrency)} is not an ISO-4217 code (invariant 4)`)
  }

  // The customer cell is where an absent field is most likely to be misread.
  // `ledger_accounts_customer_iff_wallet` makes absence a POSITIVE fact on every
  // non-wallet class, so it gets words rather than a dash — a dash means "this
  // applies and we could not read it", and here it does not apply. A `0` would
  // name customer zero, which is why `readInt` is never allowed to fall back.
  const customer = readInt(o['customer_id'])
  let customerCell: SubCell
  if (customer.kind === 'exact') {
    customerCell = knownClass && cls !== WALLET_CLASS
      ? [String(customer.value), 'unexpected — this class holds no customer']
      : [String(customer.value)]
    if (knownClass && cls !== WALLET_CLASS) {
      gaps.push(`${ref}: a customer id on class "${cls ?? ''}", which the schema CHECK forbids`)
    }
  } else if (customer.kind === 'unreadable') {
    gaps.push(`${ref}: the customer id could not be stated exactly`)
    customerCell = ['—', 'unreadable — not an absent customer']
  } else if (cls === WALLET_CLASS) {
    gaps.push(`${ref}: a wallet account with no customer id, which the schema CHECK forbids`)
    customerCell = ['—', 'missing — a wallet account must name a customer']
  } else if (knownClass) {
    customerCell = ['not a customer account']
  } else {
    // The class gap above already names this row; a second entry would count
    // the same unknown twice in the chip.
    customerCell = ['—', 'the class is unknown, so whether a customer is expected cannot be said']
  }

  const balance = asRecord(o['balance'])
  const withheld: string[] = []
  let balanceCell: Cell = '—'
  let authority: string | null = null
  if (balance === null) {
    withheld.push(
      'The account arrived with no balance figure at all, so nothing about its position is known — this is not a balance of zero.',
    )
  } else {
    const figure = balance as unknown as FigureView
    authority = typeof figure.provenance?.authority === 'string' ? figure.provenance.authority : null
    const placed = placeBalance(figure, currency)
    if (placed.ok) balanceCell = [placed.minor, placed.currency]
    else withheld.push(placed.reason)
  }
  const placedBalance = balanceCell !== '—'

  // `balance_updated_at` and `last_posting_id` are omitted together with the
  // balance, so an absent pair is the same fact the withheld reason already
  // carries. It is pointed at rather than restated, because restating it would
  // mean paraphrasing the backend's sentence into a cell.
  const updated = readInstant(o['balance_updated_at'])
  const lastPosting = readInt(o['last_posting_id'])
  if (updated.kind === 'unreadable') {
    gaps.push(`${ref}: a balance timestamp that could not be read`)
  } else if (updated.kind === 'absent' && placedBalance) {
    gaps.push(`${ref}: a placed balance with no timestamp saying when it last moved`)
  }
  if (lastPosting.kind === 'unreadable') {
    gaps.push(`${ref}: the last posting id could not be stated exactly`)
  }
  const postingSub = lastPosting.kind === 'exact'
    ? `posting ${String(lastPosting.value)}`
    : lastPosting.kind === 'unreadable' ? 'last posting id unreadable' : ''
  const movementCell: SubCell = updated.kind === 'at'
    ? [updated.label, postingSub]
    : ['—', placedBalance ? 'not stated' : 'no figure — see “Not shown” below']

  const created = readInstant(o['created_at'])
  if (created.kind !== 'at') gaps.push(`${ref}: an opening timestamp`)

  // A closed_at that is PRESENT closes the account whether or not its timestamp
  // reads — presence is the fact, the instant is the detail. Absent is the live
  // account and gets no timestamp at all, which is the whole reason the backend
  // made the field a pointer.
  const rawClosed = o['closed_at']
  const closedPresent = rawClosed !== undefined && rawClosed !== null
  const closed = readInstant(rawClosed)
  if (closedPresent && closed.kind !== 'at') {
    gaps.push(`${ref}: a closing timestamp that could not be read`)
  }
  const closedSub = !closedPresent
    ? ''
    : closed.kind === 'at' ? `closed ${closed.label}` : 'closed — timestamp could not be read'
  const openedCell: SubCell = [created.kind === 'at' ? created.label : '—', closedSub]
  const statusCell: ChipCell = closedPresent ? ['closed', 'a'] : ['open', 'n']

  return {
    cells: [
      id.kind === 'exact' ? String(id.value) : '—',
      classCell,
      scopeCell,
      currency ?? '—',
      customerCell,
      balanceCell,
      movementCell,
      openedCell,
      statusCell,
    ],
    cls,
    gaps,
    withheld,
    authority,
  }
}

// ---------------------------------------------------------------------------
// The page descriptor
// ---------------------------------------------------------------------------

interface PageRead {
  readonly limit: IntRead
  readonly returned: IntRead
  /** `null` when the backend did not send a boolean — never assumed false. */
  readonly hasMore: boolean | null
  readonly nextCursor: IntRead
}

function readPage(raw: unknown): PageRead {
  const o = asRecord(raw)
  if (o === null) {
    return { limit: { kind: 'absent' }, returned: { kind: 'absent' }, hasMore: null, nextCursor: { kind: 'absent' } }
  }
  const more = o['has_more']
  return {
    limit: readInt(o['limit']),
    returned: readInt(o['returned']),
    hasMore: typeof more === 'boolean' ? more : null,
    nextCursor: readInt(o['next_cursor']),
  }
}

/**
 * What the page descriptor lets the screen claim, and what it does not.
 *
 * A list that left rows behind and did not say so reads as the whole chart of
 * accounts, and the chart of accounts is exactly the list an operator counts.
 * So `has_more` is stated in three registers — the footer, a note, and the
 * provenance chip — and an unreadable `has_more` is never quietly read as
 * `false`: "we cannot tell whether this is complete" is a different sentence
 * from "this is complete", and only one of them is safe to act on.
 */
function pageLine(page: PageRead, shown: number): string {
  const cap = page.limit.kind === 'exact'
    ? `the server applied a page cap of ${String(page.limit.value)}`
    : 'the server did not state the page cap it applied'

  if (page.hasMore === null) {
    return `${cap} · whether more accounts exist beyond this page could not be read, so this list is not known to be complete`
  }
  if (!page.hasMore) {
    return `${cap} · no further page — this is every open account the read matched`
  }
  const cursor = page.nextCursor.kind === 'exact'
    ? `the next page continues before account ${String(page.nextCursor.value)}`
    : 'the backend sent no usable cursor for the next page'
  return `${cap} · MORE ACCOUNTS EXIST beyond these ${String(shown)} · ${cursor}`
}

function truncationNote(page: PageRead, shown: number): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Truncated',
    title: 'This is not the whole chart of accounts',
    body:
      `The backend reported more accounts beyond this page, so the ${String(shown)} rows above are a prefix and not the list. `
      + 'The account set grows with every accepted customer, so on any real deployment this is the normal state of this screen rather than an exception. '
      + 'Nothing here is totalled for the same reason: a sum of a prefix presented as a trial balance is a figure that is quietly wrong.',
    ref: pageLine(page, shown),
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * The classes on this page, counted.
 *
 * The point of the band is that an operator can see WHICH classes the page is
 * made of without reading 200 rows — on a live deployment the newest accounts
 * are customer wallets and the singleton accounts are the oldest, so an
 * id-ordered page can consist entirely of one class and nothing on the table
 * says so.
 *
 * Every card says `on this page` in its unit and repeats the caveat in its
 * posture, because these counts are counts of the rows returned and never of
 * the chart. Rows are NOT re-ordered to group them: the page's order is the
 * keyset order the cursor is expressed in, so a console that re-sorted would
 * make "the next page continues before the bottom row" false.
 */
function classCards(rows: readonly RowRead[], truncated: boolean): CardsBlock {
  const counts = new Map<string, number>()
  for (const row of rows) counts.set(row.cls ?? 'unstated', (counts.get(row.cls ?? 'unstated') ?? 0) + 1)

  const items: CardItem[] = [...counts]
    .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
    .map(([cls, n]): CardItem => {
      const known = KNOWN_CLASSES.has(cls)
      const tone: Tone = known ? 'n' : 'a'
      const sub = known
        ? ''
        : 'A ledger account class this console does not know. It renders with the value the backend sent.'
      return [
        cls,
        String(n),
        'on this page',
        truncated ? 'page count · capped' : 'page count',
        tone,
        sub,
        '',
        '',
      ]
    })

  return {
    t: 'cards',
    title: 'Classes on this page',
    // Asserted from the route rather than derived from a figure: the account
    // rows themselves carry no provenance block on the wire, and the endpoint
    // reads `ledger_accounts` in Postgres. Worth an issue on go-wallet; not
    // worth inventing a field here. The BALANCES do carry provenance, and one
    // claiming analytics authority is refused outright in `placeBalance`.
    src: 'ledger',
    note: truncated
      ? 'Counted from the rows on this page only, and the page is capped — these are not counts of the chart of accounts.'
      : 'Counted from the rows on this page only. Not a count of the chart of accounts.',
    items,
  }
}

function accountsTable(
  rows: readonly RowRead[],
  unreadableRows: number,
  page: PageRead,
  at: string,
): TableBlock {
  // Zero rendered rows is two different sentences. An empty chart is a real
  // read — a deployment with no accepted customers and no provisioned
  // singletons — and a page whose every element this console failed to parse is
  // an unread chart. Saying "the chart is empty" over the second one is a claim
  // about the ledger made from a parsing failure.
  const shown = rows.length === 0
    ? (unreadableRows === 0
        ? 'No account was returned — a read of an empty chart, not a failed read'
        : 'No row on this page could be read')
    : `${String(rows.length)} ${rows.length === 1 ? 'account' : 'accounts'} on this page`

  const disagrees = page.returned.kind === 'exact' && page.returned.value !== rows.length + unreadableRows
    ? ` · the page descriptor says ${String(page.returned.value)} rows and ${String(rows.length + unreadableRows)} arrived`
    : ''
  const lost = unreadableRows === 0
    ? ''
    : ` · ${String(unreadableRows)} row(s) could not be read at all`

  return {
    t: 'table',
    title: at === '' ? 'ledger_accounts' : `ledger_accounts · as of ${at}`,
    src: 'ledger',
    // The corpus draws facets (`class=wallet`, `currency=EUR`). This read applies
    // no filter, and a facet bar is a claim that one is applied.
    facets: false,
    note: 'Ordered by account id, newest first — the keyset order the next-page cursor is expressed in. This console does not re-sort.',
    cols: ACCOUNT_COLS,
    rows: rows.map((r) => r.cells),
    footer: `${shown}${lost}${disagrees} · ${pageLine(page, rows.length)}`,
    foot2:
      'read-only · no close, no reopen, no adjustment — a ledger balance moves only by a posting, and a posting only from a provider event. '
      + 'Closed accounts are not in this read: the endpoint returns open accounts unless asked otherwise.',
  }
}

/**
 * What this screen deliberately does not answer.
 *
 * Stated on the screen rather than left to the reader, because the corpus draws
 * a trial balance here and its absence is the kind of gap that gets read as a
 * layout bug and "fixed" by summing the page.
 */
const SCOPE_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'What is not here',
  title: 'No trial balance, no totals, and no way to change anything',
  body:
    'The endpoint sends one projected balance per account and no debit/credit split and no trial balance, so none is drawn — '
    + 'and none is computed here either, because a total taken over a capped page would be a total of the page wearing the name of the chart. '
    + 'Currencies are never summed across each other in any case. '
    + 'There is also no control on this screen by construction: the only thing that moves a ledger balance is a posting, and the only thing that writes one is a provider event.',
  ref: 'invariants 2 and 4 · nothing is re-derived that the ledger already holds',
}

function notShownNote(gaps: readonly string[], withheld: readonly string[], unreadableRows: number): NoteBlock {
  const lost = unreadableRows === 0
    ? []
    : [`${String(unreadableRows)} row(s) were not objects this console could read and are not shown as rows at all.`]
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Not shown',
    title: 'Some of this chart could not be read',
    body: [...withheld, ...gaps, ...lost].join(' · '),
    ref: 'A balance that could not be read is not a balance of zero, and an account whose class we do not know is not an ordinary account.',
  }
}

function isLedgerAccountsResponse(payload: unknown): payload is LedgerAccountsResponse {
  const o = asRecord(payload)
  return o !== null && Array.isArray(o['accounts'])
}

/**
 * Folds the response into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every absence path — an unknown
 * availability, a singleton's empty scope, a truncated page, a string amount —
 * is table-testable without a client (CLAUDE.md Part III).
 */
export function composeLedgerAccounts(response: LedgerAccountsResponse): ScreenData {
  const at = stamp(response.generated_at)
  const page = readPage(response.page)

  const rows: RowRead[] = []
  let unreadableRows = 0
  for (const [index, raw] of response.accounts.entries()) {
    const row = readAccount(raw, index)
    if (row === null) unreadableRows += 1
    else rows.push(row)
  }

  const truncated = page.hasMore === true
  const blocks: Block[] = []
  // An empty page has no classes to show, and a band of zero cards reads as a
  // chart with no account classes in it.
  if (rows.length > 0) blocks.push(classCards(rows, truncated))
  blocks.push(accountsTable(rows, unreadableRows, page, at))
  if (truncated) blocks.push(truncationNote(page, rows.length))

  const gaps = summarise(rows.flatMap((r) => r.gaps))
  const withheld = summarise(rows.flatMap((r) => r.withheld))
  if (gaps.length > 0 || withheld.length > 0 || unreadableRows > 0) {
    blocks.push(notShownNote(gaps, withheld, unreadableRows))
  }
  blocks.push(SCOPE_NOTE)

  // What the chip names, in the operator's language. Truncation belongs here:
  // the backend was asked for the chart of accounts and answered with a prefix
  // it flagged as a prefix, which is the backend telling us what it did not
  // send. A `Live` badge over the first 200 of an unknown number of accounts
  // claims to be the chart.
  const missing: string[] = [...withheld, ...gaps]
  if (unreadableRows > 0) {
    missing.push(`${String(unreadableRows)} account row(s) this console could not read`)
  }
  if (truncated) missing.push('the accounts beyond this page (the backend reported more)')
  if (page.hasMore === null) {
    missing.push('whether this page is the whole chart (the backend sent no readable has_more)')
  }
  if (at === '') missing.push('the server-side read timestamp')

  const authorities = new Set<string>()
  for (const row of rows) if (row.authority !== null) authorities.add(row.authority)
  const authority = authorities.size === 0
    ? 'unattributed'
    : [...authorities].sort((a, b) => a.localeCompare(b)).join(' + ')

  return {
    blocks,
    provenance: missing.length === 0
      ? { kind: 'live', source: SOURCE, authority, readAt: at }
      : { kind: 'partial', source: SOURCE, authority, readAt: at, missing },
  }
}

/**
 * The screen when the endpoint itself could not be read.
 *
 * One note and nothing else — never the design corpus. A corpus chart of
 * accounts is a plausible-looking list of accounts and balances that describes
 * no deployment, on a screen an operator reads to find out which accounts
 * exist.
 *
 * `unread`, not `partial`: the chip renders one word, and "Partial" says some
 * of this is real.
 */
function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  const ref = decisionRef === null ? 'Nothing on this screen was read.' : `decision_ref ${decisionRef}`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Chart of accounts',
      title: 'This screen could not be read',
      body: `${reason} No account, class or balance below is known — that is different from the chart being empty, and the screen shows none rather than guessing at any.`,
      ref,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

/**
 * Reads the chart of accounts.
 *
 * No query string. The endpoint's own defaults are the product decision — a
 * 200-row cap, and open accounts only, because "the chart of accounts an
 * operator wants is the live one and a closed account is history". The console
 * is a client and does not overrule that; it states the omission in the table
 * instead.
 *
 * Resolves rather than throwing, per the loader contract. A refusal keeps its
 * `decision_ref` (law 13) — the client surfaces it and a screen must not
 * swallow it.
 */
export const loadLedgerAccounts: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(LEDGER_ACCOUNTS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The chart of accounts endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    // An AbortError is a navigation away, not a failure to report. Let it
    // propagate so the caller's cancellation stays a cancellation.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isLedgerAccountsResponse(payload)) {
    return unreadableScreen('The chart of accounts endpoint returned a shape this console does not know.', null)
  }
  return composeLedgerAccounts(payload)
}
